<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>
    <div id="blog-content">
      <div v-if="!pending && blogDetail">
        <div id="page-content">
          <NuxtLayout
            v-if="blogDetail.image.img_full"
            name="stretch"
            class="h-full max-h-[607px] justify-center flex my-8"
          >
            <NuxtImg
              loading="lazy"
              :src="blogDetail.image.img_full"
              class="rounded aspect-video object-cover"
              alt=""
              width="1280"
              height="607"
            />
          </NuxtLayout>

          <NuxtLayout name="center">
            <h1 class="px-4 md:px-0">{{ blogDetail.title }}</h1>
            <BlogPageSection :blogDetail="blogDetail" showSocialSharing></BlogPageSection>
          </NuxtLayout>
        </div>
        <div id="blog-author-info" class="bg-primary/5 border-primary-light border-t">
          <NuxtLayout name="center">
            <div class="flex-col md:flex md:flex-row md:space-x-14 container">
              <div class="basis-[400px] pt-4 pb-1 md:border-r md:border-primary-light">
                <h6 class="font-bold text-primary">
                  {{ $t("blog.authorTitle") }}
                </h6>
                <div class="mt-4">
                  <div class="flex mb-4">
                    <BaseAvatar :img="blogDetail.author.image ?? ''" class="mr-4 w-20"></BaseAvatar>
                    <div class="flex flex-col justify-center font-semibold">
                      <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="authorUrl">
                        <div class="text-lg leading-5 font-semibold text-secondary">
                          {{ blogDetail.author.display_name }}
                        </div>
                      </NuxtLinkLocale>

                      <span class="text-dark-blue-light text-sm">{{ blogDetail.author.designation }}</span>
                    </div>
                  </div>
                  <p class="text-blog-14-sm text-left pr-12">
                    {{ blogDetail.author.short_info }}
                  </p>
                  <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="authorUrl">
                    <button class="border font-bold my-8 text-secondary text-xs rounded px-10 py-1 border-secondary">
                      {{ $t("blog.authourBio") }}
                    </button>
                  </NuxtLinkLocale>
                </div>
                <div class="md:space-y-4 md:mb-12 md:border-none border-b border-primary-light hidden">
                  <h6 class="font-bold text-primary my-4">
                    {{ $t("blog.shareText") }}
                  </h6>
                  <BlogSocialSharing width="w-10"></BlogSocialSharing>
                </div>
              </div>
              <div class="basis-2/7 py-4">
                <h6 class="font-bold text-primary">
                  {{ $t("blog.relatedText") }}
                </h6>
                <div class="mb-8 mt-1 font-bold text-blog-22">
                  {{
                    $t("blog.moreCategoryPost", {
                      category: blogDetail.categories.name,
                    })
                  }}
                </div>

                <div v-for="(blogList, index) in blogLists.data" :key="index" class="md:flex-row flex-col flex my-8">
                  <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="`/blog/categories/${blogList.categories.slug}`">
                    <span class="text-primary text-blog-14 block md:hidden font-bold">
                      {{ blogList.categories.name }}
                    </span>
                  </NuxtLinkLocale>
                  <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="`/blog/${blogList.slug}`" class="md:shrink-0">
                    <NuxtImg
                      loading="lazy"
                      class="w-full md:w-36 lg:w-48 h-auto max-h-[180px] md:max-h-[90px] object-cover aspect-video rounded"
                      :src="blogList.image.img_large"
                      alt="Sunset in the mountains"
                      width="192"
                      height="90"
                    />
                  </NuxtLinkLocale>

                  <div class="flex flex-col md:ml-4 font-bold">
                    <NuxtLinkLocale
                      :locale="switchBlogLocale(true)"
                      :to="`/blog/categories/${blogList.categories.slug}`"
                    >
                      <span class="text-primary text-blog-12-sm hidden md:block">{{ blogList.categories.name }}</span>
                    </NuxtLinkLocale>
                    <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="`/blog/${blogList.slug}`">
                      <div class="text-secondary">{{ blogList.title }}</div>
                    </NuxtLinkLocale>
                  </div>
                </div>
              </div>
            </div>
          </NuxtLayout>
        </div>
      </div>
      <div id="display-progressbar" class="w-0 h-0"></div>
    </div>
  </div>
</template>

<script setup>
import { useGlobalStore } from "@/stores/global";
const globalStore = useGlobalStore();
const route = useRoute();
const blogDetail = ref({});
defineI18nRoute({
  locales: ["en", "es"],
});
const { data: results, pending, refresh } = await useBlogDetail(route.params.slug);
blogDetail.value = Object.assign({}, ...results.value.data);
watch(
  () => route.params.slug,
  () => {
    refresh();
  }
);

const queryString = computed(() => {
  let values = `?paged=1&posts_per_page=3`;
  values += `&orderby=date&order=DESC&category_id=${blogDetail.value.categories.id}`;
  return values;
});
const { data: blogLists, error: errorMessage } = await useBlogList(queryString);

const authorUrl = `/blog/authors/${blogDetail.value.author.slug}`;

useBlogMetaTitles(blogDetail.value.meta);
const breadcrumbs = getBreadcrumbs("blog-detail", blogDetail.value);
</script>
